<template>
  <ContentSection spacing="none" class="pb-16 transition-[height] sm:pb-28" :class="{ 'pt-12 sm:pt-18': index > 0 }">
    <!-- FILTER -->
    <OverviewMagazineFilter :categories="magazineCategories" :tags="data.magazine_tags" v-model="filter" :amount-entries="(entries ?? []).length" @category="filter.category = $event" @tag="filter.tag = $event" />

    <Container useFullWidth class="pt-18">
      <!-- ARTICLES -->
      <TransitionGroup
        tag="ul"
        name="list"
        class="grid gap-x-default gap-y-default grid-cols-[repeat(auto-fill,_minmax(25rem,_1fr))]"
      >
        <li v-for="article in entries" :key="article?.id">
          <CardMagazineArticle :data="article || []" />
        </li>
      </TransitionGroup>

      <!-- LOAD MORE TRIGGER -->
      <div
        v-show="hasMorePages"
        ref="loadMoreTrigger"
        class="mx-auto mt-24 w-fit rounded bg-primary-900 p-3"
      >
        <div class="flex items-center justify-center gap-1">
          <div class="size-[0.4375rem] animate-bounce rounded-full bg-white" />
          <div class="size-[0.4375rem] animate-bounce rounded-full bg-white [animation-delay:0.25s]" />
          <div class="size-[0.4375rem] animate-bounce rounded-full bg-white [animation-delay:0.5s]" />
        </div>
        <!-- <IconLoad class="w-7" /> -->
      </div>

      <!-- NO RESULTS -->
      <Transition name="fade-in">
        <NoEntriesFound v-if="!entries?.length" />
      </Transition>
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
// Scroll Trigger to load more entries
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { MagazineArticleFragment, MainComponentsOverviewMagazineFragment } from '#gql'

const { t } = useI18n()

gsap.registerPlugin(ScrollTrigger)

// on automatic mode data.entries are added by [statamic-repo]/app/Fieldtypes/Components.php
const props = defineProps<{
    data: MainComponentsOverviewMagazineFragment
    index: number
}>()

const route = useRoute()
const { locale } = useI18n()

// Filter
export type FilterType = {
  category: string | null
  tag: string | null
}
const filter = ref<FilterType>({
  category: route.query.category ? (route.query.category as string) : 'all',
  tag: route.query.tag ? (route.query.tag as string) : null,
})

// Pagination (scroll-driven)
const limit = 12
const page = ref(1)
const hasMorePages = ref<boolean>(props.data.has_more_pages ?? false)

// Entries
const entries = ref<MainComponentsOverviewMagazineFragment['entries']>(props.data.entries)

// If there is a filter set as url query param, fetch entries with that filter
if (route.query.category || route.query.tag) {
  const payload = await useAsyncGql({
    operation: 'MagazineArticles',
    variables: {
      sort: 'date desc',
      limit: limit,
      page: page.value,
      filter: {
        site: locale.value,
        category: { contains: route.query.category === 'all' ? null : route.query.category },
        tags: { contains: route.query.tag },
      },
    },
  })

  if (payload.data.value?.entries?.data) {
    entries.value = payload.data.value.entries.data
    hasMorePages.value = payload.data.value.entries.has_more_pages
  }
}

const magazineCategories = computed(() => {
  return [
    {
      id: 'all',
      slug: 'all',
      title: t('all'),
    },
    ...props.data.magazine_categories ?? [],
  ]
})

// Load Entries on filter change
const GqlInstance = useGql()
async function fetchEntries(loadMore = false) {
  const category = filter.value.category === 'all' ? null : filter.value.category
  const payload = await GqlInstance('MagazineArticles', {
    sort: 'date desc',
    limit: limit,
    page: page.value,
    filter: {
      site: locale.value,
      category: { contains: category },
      tags: { contains: filter.value.tag },
    },
  })

  if (!payload?.entries?.data) {
    console.error('Magazine Articles could not be fetched')
    return
  }

  hasMorePages.value = payload.entries.has_more_pages

  if (loadMore) {
      // If load more, append to entries
    entries.value = [...entries.value ?? [], ...payload.entries.data]
  } else {
      // If just filter change, replace entries
    entries.value = payload.entries.data as MagazineArticleFragment[]
  }

  // Refresh scroll trigger to reset the trigger position
  await nextTick()
  ScrollTrigger.refresh()
}

// Load more entries on scroll
function loadMoreEntries() {
  if (!hasMorePages.value) return
  page.value++
  fetchEntries(true)
}

// Saves the filter in the URL
const router = useRouter()
function saveFilterInUrl() {
  router.push({
    query: {
      category: filter.value.category,
      tag: filter.value.tag,
    },
  })
}

// Watch for filter changes
watch(
  () => filter.value,
  () => {
    saveFilterInUrl()
    // Reset page to 1
    page.value = 1
    fetchEntries()
  },
  { deep: true },
)


const isMounted = ref(false)
let scrollTrigger = <any> null
const loadMoreTrigger = ref<HTMLElement | null>(null)
onMounted(() => {
  // To hide the collapse before it is mounted (workaround)
  isMounted.value = true

  // Scroll Trigger
  scrollTrigger = ScrollTrigger.create({
    trigger: loadMoreTrigger.value,
    start: 'top bottom', // Fires when the top of the trigger hits the bottom of the viewport
    end: 'bottom bottom', // Fires when the bottom of the trigger hits the bottom of the viewport
    onEnter: () => loadMoreEntries(),
  })
})

onUnmounted(() => {
  if (scrollTrigger) {
    scrollTrigger.kill()
  }
})
</script>
